/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

/**
 * Splits a string on newlines that are not inside quotes and replaces newlines and other escape sequences inside quotes with their escaped versions.
 * Keeps the escape characters inside quotes for display by replacing e.g. \n with \\n.
 * @param input - The string to split.
 * @returns An array of strings, each representing a segment of the input split on unquoted newlines.
 */
export function prepareTextWithEscapeSequence(input: string): string[] {
  const segments = [];
  let currentSegment = [];
  let inQuotes = false;
  let prevChar = '';

  for (let i = 0; i < input.length; i += 1) {
    let char = input[i];
    const isQuote = char === '"' && prevChar !== '\\';

    // Toggle the inQuotes flag if a non-escaped quote is encountered
    if (isQuote) {
      inQuotes = !inQuotes;
    }

    // Check if the current character is a newline and not inside quotes
    if (char === '\n' && !inQuotes) {
      // Join the current segment and push to segments
      segments.push(currentSegment.join(''));
      currentSegment = [];
    } else {
      // Replace escape sequences with their escaped versions
      if (inQuotes) {
        const escapeSequences: { [key: string]: string } = {
          '\n': '\\n',
          '\t': '\\t',
          '\r': '\\r',
          '\b': '\\b',
          '\f': '\\f'
        };
        char = escapeSequences[char] || char;
      }
      // Add character to current segment
      currentSegment.push(char);
    }

    prevChar = char; // Update prevChar to the current character for escape checks
  }

  // Add the last segment if anything is left
  if (currentSegment.length > 0) {
    segments.push(currentSegment.join(''));
  }

  return segments;
}
